"use client";

// ============================================================
// CLUBHUB – Termin anlegen und bearbeiten
// ============================================================
// Ein Blatt statt eines Dialogs: ein Termin hat zu viele Teile fuer
// 512 Pixel (Wann, Wiederholung, Wer, Sichtbarkeit, Zusagen,
// Helfer). Links was und wann, rechts wer und wie — auf dem Telefon
// untereinander in dieser Reihenfolge. Der Speichern-Knopf klebt
// unten, damit er bei jeder Laenge erreichbar bleibt.
//
// BESSER ALS CLUBDESK: das Formular wird nicht vom Ansehen
// „schmutzig“ — gefragt wird nur, wenn sich wirklich etwas geaendert
// hat, und die vorgeschlagene Antwort ist „weiter bearbeiten“, nicht
// „speichern“. Serien, Erinnerung und Zusage-Abfrage sind da, ohne
// Upgrade-Wand.
//
// Vorgaben wie in ClubDesk: „Vielleicht“ erlaubt, Frist bis
// Terminbeginn, ohne Teilnehmer betrifft der Termin alle Mitglieder.
// ============================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { CalendarCheck, Globe, Plus, Save, ShieldCheck, Trash2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitleRow } from "@/components/ui/card";
import { DialogChoice } from "@/components/ui/dialog";
import { FieldLabel } from "@/components/ui/field-label";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { ZeichenKnopf } from "@/components/ui/zeichen-knopf";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { VISIBILITY } from "@/lib/labels";
import { addDays, daysBetween, istTag, minutenZwischen, plusMinuten, tagVon, uhrzeit, weekdayIndex, zeitpunkt, type DayKey } from "@/lib/kalender/tag";
import { ERINNERUNGEN, regelBauen, regelLesen, wiederholungVorgabe, type Wiederholung } from "@/lib/kalender/wiederholung";
import type { KategorieView, TerminView } from "@/lib/kalender/views";
import { eingeladenZahl, type EinladungsBasis } from "@/lib/kalender/einladung";
import type { Visibility } from "@/generated/prisma/enums";
import type { ContactView } from "@/lib/views";
import { errorOf } from "@/lib/action-result";
import { terminSpeichern } from "@/app/(app)/kalender/actions";
import { cn } from "@/lib/utils";
import { KAT_PUNKT, kategorieStil } from "./marken";
import { festVorlage } from "./schichtplan";
import { WiederholungFeld } from "./wiederholung-feld";

type SchichtZeile = { id: string; title: string; von: string; bis: string; needed: string; note: string; helfer: number };

type Formular = {
  title: string;
  categoryId: string;
  description: string;
  location: string;
  tag: DayKey;
  von: string;
  bisTag: DayKey;
  bis: string;
  allDay: boolean;
  wiederholung: Wiederholung;
  exdates: DayKey[];
  groupIds: string[];
  contactIds: string[];
  visibility: Visibility;
  rsvpEnabled: boolean;
  rsvpAllowMaybe: boolean;
  fristArt: "beginn" | "zeitpunkt";
  fristTag: DayKey;
  fristZeit: string;
  maxParticipants: string;
  reminderMinutes: string;
  schichten: SchichtZeile[];
};

function ausTermin(t: TerminView | null, tag: DayKey, um: string | null): Formular {
  if (!t) {
    const von = um ?? "19:00";
    const ende = plusMinuten(zeitpunkt(tag, von), 90);
    return {
      title: "",
      categoryId: "",
      description: "",
      location: "",
      tag,
      von,
      bisTag: tagVon(ende),
      bis: uhrzeit(ende),
      allDay: false,
      wiederholung: wiederholungVorgabe(tag),
      exdates: [],
      groupIds: [],
      contactIds: [],
      visibility: "MEMBERS",
      rsvpEnabled: true,
      rsvpAllowMaybe: true,
      fristArt: "beginn",
      fristTag: tag,
      fristZeit: von,
      maxParticipants: "",
      reminderMinutes: "",
      schichten: [],
    };
  }
  const start = tagVon(t.startsAt);
  const ende = t.endsAt ?? plusMinuten(t.startsAt, 90);
  return {
    title: t.title,
    categoryId: t.categoryId ?? "",
    description: t.description ?? "",
    location: t.location ?? "",
    tag: start,
    von: uhrzeit(t.startsAt),
    bisTag: tagVon(ende),
    bis: uhrzeit(ende),
    allDay: t.allDay,
    wiederholung: regelLesen(t.rrule, start),
    exdates: t.exdates.map(tagVon),
    groupIds: t.groupIds,
    contactIds: t.contactIds,
    visibility: t.visibility,
    rsvpEnabled: t.rsvpEnabled,
    rsvpAllowMaybe: t.rsvpAllowMaybe,
    fristArt: t.rsvpDeadline ? "zeitpunkt" : "beginn",
    fristTag: t.rsvpDeadline ? tagVon(t.rsvpDeadline) : start,
    fristZeit: t.rsvpDeadline ? uhrzeit(t.rsvpDeadline) : uhrzeit(t.startsAt),
    maxParticipants: t.maxParticipants ? String(t.maxParticipants) : "",
    reminderMinutes: t.reminderMinutes ? String(t.reminderMinutes) : "",
    schichten: t.shifts.map((s) => ({ id: s.id, title: s.title, von: uhrzeit(s.startsAt), bis: uhrzeit(s.endsAt), needed: String(s.needed), note: s.note ?? "", helfer: s.helfer.length })),
  };
}

export function TerminFormular({
  termin,
  vorlage = false,
  tag,
  um,
  ab,
  kategorien,
  gruppen,
  einladung,
  kontakte,
}: {
  /** Beim Bearbeiten der Termin, beim Duplizieren die Vorlage. */
  termin: TerminView | null;
  vorlage?: boolean;
  tag: DayKey;
  um: string | null;
  /** Bearbeiten aus einem Vorkommen heraus (`?ab=`). */
  ab: DayKey | null;
  kategorien: KategorieView[];
  gruppen: { id: string; name: string; parentId: string | null; memberCount: number }[];
  /** Wer zu welcher Gruppe gehoert — fuer die Zahl der Eingeladenen. */
  einladung: EinladungsBasis;
  /** Personen fuer die Auswahl (im Musterbestand leer: die Auswahl nimmt ihre Vorgabe). */
  kontakte?: ContactView[];
}) {
  const router = useRouter();
  const anfang = useMemo(() => {
    const f = ausTermin(termin, tag, um);
    if (vorlage && termin) {
      return {
        ...f,
        title: `${f.title} (Kopie)`,
        tag,
        bisTag: addDays(tag, Math.max(0, daysBetween(f.tag, f.bisTag))),
        fristTag: tag,
        exdates: [],
        schichten: f.schichten.map((s, i) => ({ ...s, id: `neu-${i}`, helfer: 0 })),
      };
    }
    return f;
  }, [termin, tag, um, vorlage]);
  const [f, setF] = useState<Formular>(anfang);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [melden, setMelden] = useState(!termin || vorlage);
  const [speichert, setSpeichert] = useState(false);
  const bearbeiten = Boolean(termin) && !vorlage;
  const geaendert = JSON.stringify(f) !== JSON.stringify(anfang);
  const formRef = useRef<HTMLFormElement>(null);

  const setze = <K extends keyof Formular>(k: K, v: Formular[K]) => setF((alt) => ({ ...alt, [k]: v }));

  // Nur echte Aenderungen halten den Tab offen (nicht schon das Ansehen).
  useEffect(() => {
    if (!geaendert || speichert) return;
    const warnen = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warnen);
    return () => window.removeEventListener("beforeunload", warnen);
  }, [geaendert, speichert]);

  const eingeladen = useMemo(() => eingeladenZahl(einladung, f.groupIds, f.contactIds), [einladung, f.groupIds, f.contactIds]);
  const serie = f.wiederholung.art !== "keine";

  function beginnAendern(neuerTag: DayKey, neueZeit: string) {
    // Das Ende wandert mit, die Dauer bleibt.
    setF((alt) => {
      const dauer = istTag(alt.tag) && istTag(alt.bisTag) ? minutenZwischen(zeitpunkt(alt.tag, alt.von || "00:00"), zeitpunkt(alt.bisTag, alt.bis || "00:00")) : 90;
      const ende = istTag(neuerTag) ? plusMinuten(zeitpunkt(neuerTag, neueZeit || "00:00"), Math.max(0, dauer)) : null;
      const w = alt.wiederholung.art === "woechentlich" && istTag(neuerTag) && alt.wiederholung.tage.length <= 1 ? { ...alt.wiederholung, tage: [weekdayIndex(neuerTag)] } : alt.wiederholung;
      return { ...alt, tag: neuerTag, von: neueZeit, bisTag: ende ? tagVon(ende) : alt.bisTag, bis: ende ? uhrzeit(ende) : alt.bis, wiederholung: w };
    });
  }

  function pruefen(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!f.title.trim()) e.title = "Wie heißt der Termin?";
    else if (f.title.length > 120) e.title = "Höchstens 120 Zeichen.";
    if (!istTag(f.tag)) e.tag = "Bitte ein Datum wählen.";
    if (!f.allDay && !f.von) e.von = "Beginn fehlt.";
    if (!istTag(f.bisTag)) e.bisTag = "Bitte ein Datum wählen.";
    if (istTag(f.tag) && istTag(f.bisTag)) {
      const beginn = zeitpunkt(f.tag, f.allDay ? "00:00" : f.von || "00:00");
      const ende = zeitpunkt(f.bisTag, f.allDay ? "00:00" : f.bis || "00:00");
      if (ende < beginn || (!f.allDay && ende === beginn)) e.bis = "Das Ende liegt vor dem Beginn.";
    }
    if (serie) {
      if (f.wiederholung.art === "woechentlich" && f.wiederholung.tage.length === 0) e.tage = "Mindestens einen Wochentag wählen.";
      if (f.wiederholung.ende === "datum" && (!istTag(f.wiederholung.bis) || f.wiederholung.bis < f.tag)) e.bis_serie = "Das Ende der Serie liegt vor dem ersten Termin.";
      if (f.wiederholung.ende === "anzahl" && (f.wiederholung.anzahl < 2 || f.wiederholung.anzahl > 500)) e.anzahl = "Zwischen 2 und 500 Terminen.";
    }
    if (f.visibility === "GROUPS" && f.groupIds.length === 0) e.visibility = "„Gruppen“ braucht mindestens eine Gruppe.";
    if (f.rsvpEnabled && f.fristArt === "zeitpunkt") {
      if (!istTag(f.fristTag) || !f.fristZeit) e.frist = "Datum und Uhrzeit der Frist angeben.";
      else if (!serie && istTag(f.tag) && zeitpunkt(f.fristTag, f.fristZeit) > zeitpunkt(f.tag, f.allDay ? "23:59" : f.von || "00:00")) e.frist = "Die Frist liegt nach dem Beginn.";
    }
    if (f.rsvpEnabled && f.maxParticipants) {
      const n = Number(f.maxParticipants);
      if (!Number.isInteger(n) || n < 1 || n > 9999) e.maxParticipants = "Eine ganze Zahl ab 1.";
    }
    f.schichten.forEach((s, i) => {
      if (!s.title.trim()) e[`schicht-${i}`] = "Bezeichnung fehlt.";
      else if (!s.von || !s.bis) e[`schicht-${i}`] = "Beginn und Ende angeben.";
      else if (!(Number(s.needed) >= 1)) e[`schicht-${i}`] = "Bedarf ab 1 Person.";
    });
    return e;
  }

  async function absenden(ev: React.FormEvent) {
    ev.preventDefault();
    const e = pruefen();
    setFehler(e);
    if (Object.keys(e).length) {
      toast.error("Bitte die markierten Felder prüfen.");
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>("[aria-invalid=true], [data-fehler]")?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setSpeichert(true);
    const uhr = (hhmm: string) => (f.allDay ? "00:00" : hhmm || "00:00");
    const eingabe = {
      title: f.title,
      description: f.description,
      location: f.location,
      categoryId: f.categoryId || null,
      startsAt: zeitpunkt(f.tag, uhr(f.von)),
      endsAt: zeitpunkt(f.bisTag, uhr(f.bis)),
      allDay: f.allDay,
      rrule: serie ? regelBauen(f.wiederholung, f.tag) : null,
      exdates: serie ? f.exdates.map((d) => zeitpunkt(d, uhr(f.von))) : [],
      groupIds: f.groupIds,
      contactIds: f.contactIds,
      visibility: f.visibility,
      rsvpEnabled: f.rsvpEnabled,
      rsvpAllowMaybe: f.rsvpAllowMaybe,
      rsvpDeadline: f.rsvpEnabled && f.fristArt === "zeitpunkt" ? zeitpunkt(f.fristTag, f.fristZeit) : null,
      maxParticipants: f.rsvpEnabled && f.maxParticipants ? Number(f.maxParticipants) : null,
      reminderMinutes: f.reminderMinutes ? Number(f.reminderMinutes) : null,
      schichten: serie
        ? []
        : f.schichten.map((s) => {
            const start = zeitpunkt(f.tag, s.von);
            let ende = zeitpunkt(f.tag, s.bis);
            if (ende <= start) ende = zeitpunkt(addDays(f.tag, 1), s.bis);
            return { id: s.id, title: s.title, startsAt: start, endsAt: ende, needed: Number(s.needed), note: s.note || null };
          }),
    };
    let ergebnis: Awaited<ReturnType<typeof terminSpeichern>>;
    try {
      ergebnis = await terminSpeichern(bearbeiten ? termin!.id : null, eingabe);
    } catch {
      setSpeichert(false);
      toast.error("Das hat nicht geklappt — bitte noch einmal versuchen.");
      return;
    }
    const fehlerText = errorOf(ergebnis);
    if (fehlerText || !ergebnis.success) {
      setSpeichert(false);
      const feld = "field" in ergebnis ? ergebnis.field : undefined;
      if (feld) setFehler({ [feld]: fehlerText ?? "" });
      toast.error(fehlerText ?? "Das hat nicht geklappt.");
      return;
    }
    toast.success(bearbeiten ? "Termin gespeichert" : "Termin angelegt", {
      description: melden ? "Einladungen per Mail und Push kommen mit dem Nachrichtenmodul." : undefined,
    });
    router.push(bearbeiten ? `/kalender/${termin!.id}${ab ? `~${ab}` : ""}` : `/kalender?am=${f.tag}`);
    router.refresh();
  }

  async function abbrechen() {
    if (geaendert) {
      const verwerfen = await confirmDialog({
        title: "Änderungen verwerfen?",
        description: "Was du hier eingetragen hast, geht verloren.",
        confirmLabel: "Verwerfen",
        cancelLabel: "Weiter bearbeiten",
        danger: true,
      });
      if (!verwerfen) return;
    }
    setSpeichert(true);
    router.back();
  }

  const schichtVorlage = () => {
    if (!istTag(f.tag)) return;
    const s = festVorlage(f.tag, zeitpunkt(f.tag, f.von || "18:00"), zeitpunkt(f.bisTag, f.bis || "23:00"));
    setze(
      "schichten",
      s.map((x, i) => ({ id: `neu-${Date.now()}-${i}`, title: x.title, von: uhrzeit(x.startsAt), bis: uhrzeit(x.endsAt), needed: String(x.needed), note: "", helfer: 0 })),
    );
  };

  return (
    <form ref={formRef} onSubmit={absenden} noValidate className="space-y-6">
      {bearbeiten && termin?.rrule && (
        <div className="border-border bg-bg-primary text-body-sm text-text-secondary rounded-lg border px-3 py-2">
          Änderungen gelten für die ganze Serie. Ein einzelnes Vorkommen verschiebst oder sagst du im Termin selbst ab (Menü „…“).
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitleRow title="Was" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Input label="Titel" value={f.title} onChange={(e) => setze("title", e.target.value)} placeholder="z. B. Training 1. Mannschaft, Weihnachtsfeier" error={fehler.title} maxLength={140} autoFocus={!bearbeiten} />
              <div className="space-y-1.5">
                <FieldLabel>Kategorie</FieldLabel>
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Kategorie">
                  {[{ id: "", name: "Keine", color: null, sortOrder: 0 }, ...kategorien].map((k) => {
                    const an = f.categoryId === k.id;
                    return (
                      <button
                        key={k.id || "keine"}
                        type="button"
                        role="radio"
                        aria-checked={an}
                        onClick={() => setze("categoryId", k.id)}
                        style={kategorieStil(k)}
                        className={cn(
                          "text-body-sm duration-fast inline-flex h-8 items-center gap-1.5 rounded-full border px-3 transition-colors",
                          an ? "border-accent bg-accent-subtle/40 text-text-primary font-medium" : "border-border text-text-secondary hover:bg-bg-secondary",
                        )}
                      >
                        {k.id && <span aria-hidden className={cn("h-2 w-2 rounded-full", KAT_PUNKT)} />}
                        {k.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Textarea label="Beschreibung" value={f.description} onChange={(e) => setze("description", e.target.value)} rows={4} placeholder="Treffpunkt, Tagesordnung, was mitzubringen ist …" hint="Steht in der Einladung, im Termin und im Kalender-Abo." />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleRow title="Wann" />
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_8rem_1fr_8rem]">
                <Input type="date" label="Beginn" value={f.tag} onChange={(e) => beginnAendern(e.target.value, f.von)} error={fehler.tag} />
                {!f.allDay ? <Input type="time" label="Uhrzeit" value={f.von} onChange={(e) => beginnAendern(f.tag, e.target.value)} error={fehler.von} /> : <span className="max-sm:hidden" />}
                <Input type="date" label="Ende" value={f.bisTag} min={f.tag} onChange={(e) => setze("bisTag", e.target.value)} error={fehler.bisTag ?? (f.allDay ? fehler.bis : undefined)} />
                {!f.allDay && <Input type="time" label="Uhrzeit" value={f.bis} onChange={(e) => setze("bis", e.target.value)} error={fehler.bis} />}
              </div>
              <Switch checked={f.allDay} onChange={(v) => setze("allDay", v)} label="Ganztägig" hint="Für Turniere, Ausflüge, Platzsperren — auch über mehrere Tage." />
              <div className="border-border-subtle border-t pt-4">
                <WiederholungFeld
                  wert={f.wiederholung}
                  onChange={(w) => setze("wiederholung", w)}
                  start={f.tag}
                  uhr={f.allDay ? "" : f.von}
                  exdates={f.exdates}
                  onExdates={(d) => setze("exdates", d)}
                  fehler={{ tage: fehler.tage, bis: fehler.bis_serie, anzahl: fehler.anzahl }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleRow title="Wo" />
            </CardHeader>
            <CardContent>
              <Input label="Ort" value={f.location} onChange={(e) => setze("location", e.target.value)} placeholder="Straße, Ort oder Vereinsheim" hint="Mit Adresse lässt sich der Ort im Termin auf der Karte öffnen." />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleRow
                title="Helferschichten"
                action={
                  !serie && (
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={schichtVorlage}>
                        <Wand2 className="h-3.5 w-3.5" aria-hidden />
                        Vorlage Fest
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setze("schichten", [...f.schichten, { id: `neu-${Date.now()}`, title: "", von: f.von || "18:00", bis: f.bis || "20:00", needed: "2", note: "", helfer: 0 }])}
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden />
                        Schicht
                      </Button>
                    </div>
                  )
                }
              />
            </CardHeader>
            <CardContent className="space-y-3">
              {serie ? (
                <p className="text-body-sm text-text-tertiary">Helferschichten gibt es bei einzelnen Terminen — Fest, Turnier, Arbeitseinsatz.</p>
              ) : f.schichten.length === 0 ? (
                <p className="text-body-sm text-text-tertiary">Keine Schichten. Mit „Vorlage Fest“ stehen Aufbau, Ausschank, Grill und Abbau passend zur Uhrzeit da.</p>
              ) : (
                f.schichten.map((s, i) => (
                  <div key={s.id} data-fehler={fehler[`schicht-${i}`] ? "" : undefined} className="border-border-subtle space-y-1 border-b pb-3 last:border-0 last:pb-0">
                    <div className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_5rem_auto]">
                      <Input
                        aria-label="Bezeichnung der Schicht"
                        value={s.title}
                        placeholder="Bezeichnung"
                        onChange={(e) => setze("schichten", f.schichten.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                        wrapperClassName="max-sm:col-span-1"
                      />
                      <Input type="time" aria-label="Beginn" value={s.von} onChange={(e) => setze("schichten", f.schichten.map((x, j) => (j === i ? { ...x, von: e.target.value } : x)))} wrapperClassName="max-sm:order-3" />
                      <Input type="time" aria-label="Ende" value={s.bis} onChange={(e) => setze("schichten", f.schichten.map((x, j) => (j === i ? { ...x, bis: e.target.value } : x)))} wrapperClassName="max-sm:order-4" />
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        aria-label="Bedarf"
                        value={s.needed}
                        onChange={(e) => setze("schichten", f.schichten.map((x, j) => (j === i ? { ...x, needed: e.target.value } : x)))}
                        className="tabular-nums"
                        wrapperClassName="max-sm:order-5"
                      />
                      <ZeichenKnopf
                        label="Schicht entfernen"
                        className="hover:text-danger h-10 w-10 max-sm:order-2"
                        onClick={async () => {
                          if (s.helfer > 0 && !(await confirmDialog({ title: `Schicht „${s.title}“ entfernen?`, description: `${s.helfer} Eingetragene werden beim Speichern benachrichtigt.`, confirmLabel: "Entfernen", danger: true }))) return;
                          setze("schichten", f.schichten.filter((_, j) => j !== i));
                        }}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </ZeichenKnopf>
                    </div>
                    <p className={cn("text-caption", fehler[`schicht-${i}`] ? "text-danger" : "text-text-tertiary")}>
                      {fehler[`schicht-${i}`] ?? `${s.needed || "?"} Personen${s.helfer ? ` · ${s.helfer} schon eingetragen` : ""}${s.bis && s.von && s.bis <= s.von ? " · endet nach Mitternacht" : ""}`}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitleRow title="Wer" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel>Gruppen</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {gruppen.map((g) => {
                    const an = f.groupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        aria-pressed={an}
                        onClick={() => setze("groupIds", an ? f.groupIds.filter((x) => x !== g.id) : [...f.groupIds, g.id])}
                        className={cn(
                          "text-caption duration-fast inline-flex h-7 items-center gap-1 rounded-full border px-2.5 transition-colors",
                          an ? "border-accent bg-accent-subtle/40 text-text-primary font-medium" : "border-border text-text-secondary hover:bg-bg-secondary",
                        )}
                      >
                        {g.parentId ? <span className="text-text-tertiary">↳</span> : null}
                        {g.name}
                        <span className="text-text-tertiary tabular-nums">{g.memberCount}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Einzelne Personen</FieldLabel>
                <KontaktAuswahl mehrfach values={f.contactIds} onChangeMany={(ids) => setze("contactIds", ids)} placeholder="Zusätzlich einladen …" kontakte={kontakte} />
              </div>
              <p className="bg-bg-secondary text-caption text-text-secondary rounded-lg px-3 py-2 tabular-nums">
                {f.groupIds.length === 0 && f.contactIds.length === 0 ? (
                  <>
                    <span className="text-text-primary font-medium">Alle Mitglieder ({eingeladen})</span> — ohne Auswahl betrifft der Termin alle.
                  </>
                ) : (
                  <>
                    <span className="text-text-primary font-medium">{eingeladen} Personen eingeladen</span>
                    {f.groupIds.some((id) => gruppen.some((g) => g.parentId === id)) ? " — Untergruppen sind eingeschlossen." : "."}
                  </>
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleRow title="Sichtbarkeit" />
            </CardHeader>
            <CardContent className="space-y-3">
              <DialogChoice<Visibility>
                label="Wer sieht den Termin"
                value={f.visibility}
                onChange={(v) => setze("visibility", v)}
                options={(["PUBLIC", "MEMBERS", "GROUPS", "BOARD"] as const).map((v) => ({ value: v, label: VISIBILITY[v].label, hint: VISIBILITY[v].hinweis }))}
              />
              {fehler.visibility && (
                <p className="text-caption text-danger" data-fehler="">
                  {fehler.visibility}
                </p>
              )}
              {f.visibility === "PUBLIC" && (
                <p className="bg-bg-secondary text-caption text-text-secondary flex items-start gap-2 rounded-lg px-3 py-2">
                  <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  Titel, Zeit, Ort und Beschreibung erscheinen auf der Website und im öffentlichen Kalender-Abo. Wer zu- oder abgesagt hat, bleibt intern.
                </p>
              )}
            </CardContent>
          </Card>

          <Card id="zusagen">
            <CardHeader>
              <CardTitleRow title="Zu- und Absagen" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Switch checked={f.rsvpEnabled} onChange={(v) => setze("rsvpEnabled", v)} label="Zusagen abfragen" hint="Eingeladene antworten mit einem Tipp aus Mail, Push oder App — ohne Passwort." />
              {f.rsvpEnabled && (
                <>
                  <Switch checked={f.rsvpAllowMaybe} onChange={(v) => setze("rsvpAllowMaybe", v)} label="„Vielleicht“ erlauben" hint="Für Spiele mit Kader besser aus — dann gibt es nur Ja oder Nein." />
                  <div className="space-y-2" data-fehler={fehler.frist ? "" : undefined}>
                    <DialogChoice<"beginn" | "zeitpunkt">
                      label="Antworten möglich"
                      value={f.fristArt}
                      onChange={(v) => setze("fristArt", v)}
                      options={[
                        { value: "beginn", label: "Bis Terminbeginn" },
                        { value: "zeitpunkt", label: "Bis zu einer Frist", hint: serie ? "Gilt für jeden Termin der Serie mit demselben Abstand zum Beginn." : "Etwa damit der Kader am Freitag feststeht." },
                      ]}
                    />
                    {f.fristArt === "zeitpunkt" && (
                      <div className="grid grid-cols-[1fr_7rem] gap-2">
                        <Input type="date" aria-label="Frist, Datum" value={f.fristTag} onChange={(e) => setze("fristTag", e.target.value)} />
                        <Input type="time" aria-label="Frist, Uhrzeit" value={f.fristZeit} onChange={(e) => setze("fristZeit", e.target.value)} />
                      </div>
                    )}
                    {fehler.frist && <p className="text-caption text-danger">{fehler.frist}</p>}
                  </div>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    label="Höchstens Teilnehmende"
                    value={f.maxParticipants}
                    onChange={(e) => setze("maxParticipants", e.target.value)}
                    placeholder="ohne Grenze"
                    hint="Wer danach zusagt, kommt auf die Warteliste und rückt bei Absagen nach."
                    error={fehler.maxParticipants}
                    className="tabular-nums"
                  />
                </>
              )}
              <Select
                label="Erinnerung"
                value={f.reminderMinutes}
                onChange={(v) => setze("reminderMinutes", v)}
                options={[{ value: "", label: "Keine" }, ...ERINNERUNGEN.map((e) => ({ value: String(e.minuten), label: e.label }))]}
                hint={f.rsvpEnabled ? "An alle, die nicht abgesagt haben — wer noch offen ist, bekommt den Zu-/Absage-Knopf dazu." : "An alle Eingeladenen."}
              />
            </CardContent>
          </Card>

          <p className="text-caption text-text-tertiary flex items-start gap-2 px-1">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            Zusagen und Anwesenheit sehen Vorstand und Übungsleitung. Personen ohne Einwilligung „Internet und Social Media“ erscheinen in keiner öffentlichen Liste.
          </p>
        </div>
      </div>

      {/* Fuss: klebt unten, ueber der Handyleiste */}
      <div className="bg-bg-primary/95 border-border shadow-e2 sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 backdrop-blur lg:bottom-3">
        <Switch checked={melden} onChange={setMelden} label={bearbeiten ? "Eingeladene über Änderungen informieren" : "Einladungen versenden"} hint={`${eingeladen} Personen per E-Mail und Push`} />
        <span className="flex-1" aria-hidden />
        <Button type="button" variant="ghost" onClick={abbrechen}>
          Abbrechen
        </Button>
        <Button type="submit" loading={speichert}>
          {bearbeiten ? <Save className="h-4 w-4" aria-hidden /> : <CalendarCheck className="h-4 w-4" aria-hidden />}
          {bearbeiten ? "Speichern" : "Termin anlegen"}
        </Button>
      </div>
    </form>
  );
}
